/**
 * InlineGuideContent
 *
 * AI Guide business logic wrapper for use outside the video.js plugin.
 * Rendered inside AppDrawer by DetailVideoPlayer in mobile portrait mode.
 *
 * Contains:
 * - PostHog eligibility check via Redux guideEnabled selector
 * - StaticTextProvider and EventContextProvider wrapping
 * - Chat subscription, session lookup, cache updates
 * - PostHog and GA4 event tracking
 * - Toast nudge logic (concept moments)
 * - Scroll indicator / error toast state
 * - sendMessage with analytics
 * - handleClose with session duration tracking
 * - Auto-open after 1 second
 *
 * Does NOT contain (handled by AppDrawer or mount point):
 * - player.addClass/removeClass calls
 * - vjs-portrait-drawer class toggling
 * - Body scroll lock (AppDrawer owns this)
 * - visualViewport resize handler (useKeyboardAdjust at mount point)
 * - useBottomSheetDrag (AppDrawer owns this)
 * - Fullscreen change handler
 * - Mouse/keyboard event activity reporting to player
 */

import React, {
  RefObject,
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { fromJS } from 'immutable'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import {
  EN, DE, DE_DE, ES, ES_LA, FR, FR_FR,
} from '../../services/languages/constants'
import { isIOS, isNonAmazonAndroid } from '../../environment/index'
import { selectOracleGuideOnSideClosed, selectOracleToastDismissedContentId } from '../../services/oracle/selectors'
import { setOracleGuideOnSideClosed } from '../../services/oracle/actions'
import { getBoundActions } from '../../actions/index'
import {
  AI_CONVERSATION_STARTED_GA4_EVENT,
  AI_CONVERSATION_RESUMED_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_MESSAGE_RECEIVED_GA4_EVENT,
  AI_MESSAGE_FAILURE_GA4_EVENT,
  AI_ERROR_RESPONSE_GA4_EVENT,
  AI_MODULE_OPENED_GA4_EVENT,
  AI_MODULE_CLOSED_GA4_EVENT,
  AI_MODULE_VIEWED_GA4_EVENT,
  AI_MODULE_CLICKED_GA4_EVENT,
} from '../../services/event-tracking/index'
import {
  EventContextProvider,
  usePostHogEvent,
} from '../PostHog/index'
import { OracleGuideOnSideLayout } from './OracleGuideOnSideLayout'
import { OracleGuideToastNudge } from './OracleGuideToastNudge'
import { useGotsChatSubscription, type GotsSubscriptionDocuments } from './hooks'
import { useConceptMoments } from './hooks/useConceptMoments'
import { useConceptTrigger } from './hooks/useConceptTrigger'
import { useGetChatSessionByVideoLazyQuery } from './queries.generated'
import { cacheStreamedMessage } from './cacheStreamedMessage'
import { useIntersectionObserver } from '../OracleCommons/useIntersectionObserver'
import {
  StaticTextProvider,
  InternationalStrings,
  useTranslatedText,
} from '../OracleCommons/StaticTextContext'

// Language files for GotS-specific translations
import langEn from './lang_en.json'
import langDe from './lang_de-DE.json'
import langEs from './lang_es-LA.json'
import langFr from './lang_fr-FR.json'

const TMP_PERSONALITY_ID = 2

const getStaticTextForLanguage = (language: string | null): InternationalStrings => {
  switch (language) {
    case DE:
    case DE_DE:
      return fromJS({ data: langDe }) as InternationalStrings
    case ES:
    case ES_LA:
      return fromJS({ data: langEs }) as InternationalStrings
    case FR:
    case FR_FR:
      return fromJS({ data: langFr }) as InternationalStrings
    case EN:
    default:
      return fromJS({ data: langEn }) as InternationalStrings
  }
}

export interface InlineGuideContentProps {
  contentId: string | null
  featureMediaId: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  player?: any
  toastPortalRef?: RefObject<HTMLElement | null>
}

/**
 * Inner component with chat logic and analytics.
 * Must be rendered inside StaticTextProvider and EventContextProvider.
 */
const InlineGuideContentInner: React.FC<InlineGuideContentProps> = ({
  contentId,
  featureMediaId,
  player,
  toastPortalRef,
}) => {
  const dispatch = useDispatch()
  const isClosed = useSelector(selectOracleGuideOnSideClosed)

  // Get JWT from Redux auth state
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt') || ''

  // GA4 event action
  const { setAiGa4Event } = getBoundActions(dispatch).eventTracking

  // PostHog event tracking hooks
  const trackModuleOpened = usePostHogEvent('ai_module_opened')
  const trackModuleClosed = usePostHogEvent('ai_module_closed')
  const trackModuleViewed = usePostHogEvent('ai_module_viewed')
  const trackMessageReceived = usePostHogEvent('ai_message_received')
  const trackVideoStarted = usePostHogEvent('video_started')
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const trackErrorResponse = usePostHogEvent('ai_error_response')
  const trackMessageFailure = usePostHogEvent('ai_message_failure')
  const trackConversationStarted = usePostHogEvent('ai_conversation_started')
  const trackConversationResumed = usePostHogEvent('ai_conversation_resumed')
  const trackModerationViolation = usePostHogEvent('ai_moderation_violation')
  const trackModuleClicked = usePostHogEvent('ai_module_clicked')
  const trackToastShown = usePostHogEvent('concept_moment_toast_shown')
  const trackToastClicked = usePostHogEvent('concept_moment_toast_clicked')
  const trackToastDismissed = usePostHogEvent('concept_moment_toast_dismissed')

  const fallbackPrompt = useTranslatedText({ keys: ['toastNudge', 'fallbackPrompt'] })

  // Tracking refs
  const hasOpenedBeforeRef = useRef(false)
  const openedAtRef = useRef<number | null>(null)
  const prevContentIdRef = useRef<string | null>(null)
  const conversationEventFiredRef = useRef(false)
  const moduleViewedFiredRef = useRef(false)

  const apolloClient = useApolloClient()
  const language = useSelector(selectPrimaryLanguage) || EN

  // Track ai_module_opened on every open transition. Auto-open on
  // initial render fires with is_initial: true; manual re-opens fire
  // with is_initial: false.
  useEffect(() => {
    if (!isClosed) {
      const isInitial = !hasOpenedBeforeRef.current
      hasOpenedBeforeRef.current = true
      openedAtRef.current = Date.now()

      trackModuleOpened('Video Guide', isInitial)
      setAiGa4Event(AI_MODULE_OPENED_GA4_EVENT.mergeDeep({
        is_initial: isInitial,
      }))
    }
  }, [isClosed, trackModuleOpened, setAiGa4Event])

  // Auto-open is handled by DetailVideoPlayer (outside AppDrawer) so it
  // fires even before this component mounts.

  // Track video_started when video changes
  useEffect(() => {
    if (contentId && contentId !== prevContentIdRef.current) {
      if (prevContentIdRef.current !== null) {
        trackVideoStarted()
      }
      prevContentIdRef.current = contentId
    }
  }, [contentId, trackVideoStarted])

  // Chat session state
  const [chatSessionId, setChatSessionId] = useState<string | null>(null)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const scrollToBottomRef = useRef<(() => void) | null>(null)
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  // Toast nudge (concept moments)
  const { moments: conceptMoments } = useConceptMoments()
  const dismissedContentId = useSelector(selectOracleToastDismissedContentId)
  const isDismissed = dismissedContentId === contentId

  const {
    shouldShowToast,
    activeMoment,
    dismiss: dismissToast,
  } = useConceptTrigger({
    moments: conceptMoments,
    isGuideOpen: !isClosed,
    isDismissed,
  })

  // Track toast shown event
  const prevShownMomentRef = useRef<number | null>(null)
  useEffect(() => {
    if (shouldShowToast && activeMoment
        && prevShownMomentRef.current !== activeMoment.startSeconds) {
      prevShownMomentRef.current = activeMoment.startSeconds
      trackModuleViewed('Toast Message')
      trackToastShown(activeMoment.startSeconds, activeMoment.conceptLabel)
      setAiGa4Event(AI_MODULE_VIEWED_GA4_EVENT.mergeDeep({
        ui_element: 'Toast Message',
      }))
    }
  }, [shouldShowToast, activeMoment, trackModuleViewed, trackToastShown, setAiGa4Event])

  useEffect(() => {
    prevShownMomentRef.current = null
  }, [contentId])

  // Session lookup
  const [lookupSession, { data: sessionData }] = useGetChatSessionByVideoLazyQuery()

  useEffect(() => {
    if (!contentId || !featureMediaId) {
      setChatSessionId(null)
      return
    }
    lookupSession({
      variables: { contentId, featureMediaId },
      context: { jwt },
    })
  }, [contentId, featureMediaId, lookupSession, jwt])

  useEffect(() => {
    const foundSession = sessionData?.oracleChatSessionList?.oracleChatSessions?.[0]
    if (foundSession?.id) {
      setChatSessionId(foundSession.id)
    } else if (sessionData && !foundSession) {
      setChatSessionId(null)
    }
  }, [sessionData])

  // Handle streaming completion - update Apollo cache
  const handleMessageComplete = useCallback((data: {
    messagePairId: string
    chatSessionId: string
    sessionTitle?: string | null
    userMessage: string
    oracleMessage: string
    documents: GotsSubscriptionDocuments | null
  }) => {
    trackMessageReceived(data.messagePairId, data.oracleMessage.length, 'Text')
    setAiGa4Event(AI_MESSAGE_RECEIVED_GA4_EVENT.mergeDeep({
      message_pair_id: data.messagePairId,
      response_length: data.oracleMessage.length,
    }))
    cacheStreamedMessage({
      apolloClient,
      chatSessionId: data.chatSessionId,
      messagePairId: data.messagePairId,
      userMessage: data.userMessage,
      oracleMessage: data.oracleMessage,
      documents: data.documents,
      language,
    })
  }, [apolloClient, language, trackMessageReceived, setAiGa4Event])

  // Chat subscription
  const {
    streamingText,
    isStreaming,
    pendingMessage,
    sendMessage: sendMessageToSubscription,
    stopStreaming,
    documents: streamingDocuments,
    moderationError,
  } = useGotsChatSubscription({
    personalityId: TMP_PERSONALITY_ID.toString(),
    jwt,
    chatSessionId,
    contentId,
    featureMediaId,
    language,
    onSessionReceived: (sessionId) => setChatSessionId(sessionId),
    onMessageComplete: handleMessageComplete,
    onError: (err) => {
      setErrorMessage(err.message)
      setShowErrorToast(true)
    },
    onBackendError: (errorCode, errMsg) => {
      trackErrorResponse(errMsg, 'Text', 'Text Field')
      setAiGa4Event(AI_ERROR_RESPONSE_GA4_EVENT.mergeDeep({
        error_code: errorCode,
        error_message: errMsg,
      }))
    },
    onClientError: (errMsg) => {
      trackMessageFailure(errMsg, 'Text', 'Text Field')
      setAiGa4Event(AI_MESSAGE_FAILURE_GA4_EVENT.mergeDeep({
        error_message: errMsg,
      }))
    },
    onModerationError: (categories, severity) => {
      trackModerationViolation(severity, categories)
    },
  })

  // Wrapped sendMessage with analytics
  const sendMessage = useCallback((message: string) => {
    if (!conversationEventFiredRef.current) {
      conversationEventFiredRef.current = true
      if (chatSessionId) {
        trackConversationResumed('Text', 'Text Field')
        setAiGa4Event(AI_CONVERSATION_RESUMED_GA4_EVENT)
      } else {
        trackConversationStarted('Text', 'Video Guide')
        setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
          ui_element: 'Text Field',
        }))
      }
    }
    trackMessageSent(message, 'Text', 'Text Field')
    setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: 'Text Field',
      input_method: 'text sent',
    }))
    sendMessageToSubscription(message)
  }, [
    chatSessionId,
    trackMessageSent,
    trackConversationStarted,
    trackConversationResumed,
    sendMessageToSubscription,
    setAiGa4Event,
  ])

  // Container ref for intersection observer
  const chatContainerRef = useRef<HTMLDivElement | null>(null)

  // Track ai_module_viewed when container is visible
  const handleModuleViewed = useCallback(() => {
    if (moduleViewedFiredRef.current) return
    moduleViewedFiredRef.current = true
    trackModuleViewed('Video Guide')
    setAiGa4Event(AI_MODULE_VIEWED_GA4_EVENT)
  }, [trackModuleViewed, setAiGa4Event])

  useIntersectionObserver(chatContainerRef, handleModuleViewed, { enabled: true })

  // Toast action handler
  const handleToastAction = useCallback(() => {
    const prompt = activeMoment
      ? `Dive deeper on ${activeMoment.conceptLabel}`
      : fallbackPrompt

    trackModuleClicked('Toast Message')
    if (activeMoment) {
      trackToastClicked(activeMoment.startSeconds, activeMoment.conceptLabel)
    }
    setAiGa4Event(AI_MODULE_CLICKED_GA4_EVENT.mergeDeep({
      ui_element: 'Toast Message',
    }))

    dispatch(setOracleGuideOnSideClosed(false))
    dismissToast()

    if (prompt) {
      sendMessage(prompt)
    }
  }, [
    activeMoment, fallbackPrompt, sendMessage,
    dispatch, dismissToast, trackModuleClicked, trackToastClicked, setAiGa4Event,
  ])

  // Toast dismiss handler
  const handleToastDismiss = useCallback((method: 'manual' | 'auto') => {
    trackModuleClosed(null, 'Toast Message', method)
    if (activeMoment) {
      trackToastDismissed(activeMoment.startSeconds, activeMoment.conceptLabel)
    }
    setAiGa4Event(AI_MODULE_CLOSED_GA4_EVENT.mergeDeep({
      ui_element: 'Toast Message',
      method,
    }))
    dismissToast()
  }, [activeMoment, dismissToast, trackModuleClosed, trackToastDismissed, setAiGa4Event])

  // Close handler
  const handleClose = useCallback(() => {
    const sessionDurationMs = openedAtRef.current
      ? Date.now() - openedAtRef.current
      : null
    trackModuleClosed(sessionDurationMs, 'Video Guide')
    setAiGa4Event(AI_MODULE_CLOSED_GA4_EVENT.mergeDeep({
      session_duration_ms: sessionDurationMs,
    }))
    dispatch(setOracleGuideOnSideClosed(true))
  }, [dispatch, trackModuleClosed, setAiGa4Event])

  const handleScrollIndicatorChange = useCallback((
    visible: boolean,
    scrollToBottom: () => void,
  ) => {
    setShowScrollIndicator(visible)
    scrollToBottomRef.current = scrollToBottom
  }, [])

  const toastPortalEl = toastPortalRef?.current

  return (
    <>
      {toastPortalEl && createPortal(
        <OracleGuideToastNudge
          isVisible={shouldShowToast}

          onAction={handleToastAction}
          onDismiss={handleToastDismiss}
        />,
        toastPortalEl,
      )}
      <OracleGuideOnSideLayout
        streamingText={streamingText}
        isStreaming={isStreaming}
        pendingMessage={pendingMessage}
        sendMessage={sendMessage}
        stopStreaming={stopStreaming}
        containerRef={chatContainerRef}
        chatSessionId={chatSessionId}
        onClose={handleClose}
        showScrollIndicator={showScrollIndicator}
        onScrollToBottom={() => scrollToBottomRef.current?.()}
        onScrollIndicatorChange={handleScrollIndicatorChange}
        showErrorToast={showErrorToast}
        errorMessage={errorMessage}
        onErrorToastClose={() => {
          setShowErrorToast(false)
          setErrorMessage(undefined)
        }}
        moderationError={moderationError}
        player={player}
        streamingDocuments={streamingDocuments}
        activeMoment={activeMoment}
        hideDragHandle
        useOverlay={isIOS() || isNonAmazonAndroid()}
      />
    </>
  )
}

/**
 * InlineGuideContent — AI Guide for use outside the video.js plugin.
 *
 * No eligibility gate here — the parent (DetailVideoPlayer) already gates
 * on the oracleInlineGuide config flag + isMobilePortrait. If this component
 * mounts, the user should see it.
 */
export const InlineGuideContent: React.FC<InlineGuideContentProps> = (props) => {
  const language = useSelector(selectPrimaryLanguage)

  const staticText = useMemo(
    () => getStaticTextForLanguage(language),
    [language],
  )

  return (
    <StaticTextProvider staticText={staticText}>
      <EventContextProvider context="ai_guide_on_the_side">
        <InlineGuideContentInner {...props} />
      </EventContextProvider>
    </StaticTextProvider>
  )
}
